import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  fetchVitalSignResult,
  IVitalSign,
  resetVitalSigns,
  selectLastVitalSigns,
  selectNoRecordVitalSigns,
  selectVitalSignResults,
  selectVitalSignsStatus,
  selectVitalSignsType,
} from '../../store/slices/vitalSigns';
import { useTranslation } from '../../hooks/useTranslations';
import VitalSignResultsCard from '../VitalSignsResults/VitalSignResultsCard';
import CollapseV2 from './CollapseV2';
import BackButton from '../../components/UI/Buttons/BackButton';
import WithLoader from '../../components/WithLoader/WithLoader';
import CustomPagination from '../../components/UI/CustomPagination/CustomPagination';
import Modal from '../../components/UI/Modals/Modal';
import SelectTimeFrame from '../../components/SelectTimeFrame/SelectTimeFrame';
import VitalSignReport from './VitalSignReport/VitalSignReport';
import MainVitalScreen from './MainVitalScreen/MainVitalScreen';
import MeasurementTimes from '../../components/MeasurmentTime/MeasurmentTime';
import ManualRecord from './ManualRecord/ManualRecord';
import downloadIcon from '../../assets/icons/download-icon.svg';
import moment from 'moment';
import styles from './VitalSignResultsV2.module.scss';
import _ from 'lodash';

interface VitalSignResultsV2Props {
  accountNumber: string | undefined;
  personAccountNumber?: number;
}

type ScreenTypes = 'list' | 'report' | 'measurementTime' | 'manualRecord';

const VitalSignResultsV2 = ({ accountNumber, personAccountNumber }: VitalSignResultsV2Props) => {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();

  const vsResults = useAppSelector(selectVitalSignResults);
  const lastVitalsigns = useAppSelector(selectLastVitalSigns);
  const vsResultsStatus = useAppSelector(selectVitalSignsStatus);
  const vsResultsType = useAppSelector(selectVitalSignsType);
  const noRecordVitalSigns = useAppSelector(selectNoRecordVitalSigns);
  const [showDetails, setShowDetails] = useState<{ groupName: string; groupId: number }>();
  const [vitalSignReport, setVitalSignReport] = useState<null | Array<IVitalSign>>(null);
  const [screen, setScreen] = useState<ScreenTypes>('list');
  const [openModal, setOpenModal] = useState(false);

  const lang = localStorage.getItem('lang') || 'en';
  const detailedData: Record<string, any[]> = _.groupBy(
    vsResults[showDetails ? showDetails.groupName : ''],
    (item) => moment(item.datetime_measure).locale(lang).format('DD MMMM YYYY')
  );

  const handleVitalSignClick = (results: Array<IVitalSign>) => {
    setVitalSignReport(results);
    setScreen('report');
  };

  const handleSetReport = (
    group: { groupName: string; groupId: number },
    data: Array<IVitalSign>
  ) => {
    setShowDetails(group);
    setScreen('report');
    setVitalSignReport(data);
  };

  useEffect(() => {
    dispatch(
      fetchVitalSignResult({
        account_number: accountNumber,
        ...(showDetails?.groupId && {
          vital_sign_group_id_out: showDetails.groupId,
        }),
      })
    );
    return () => {
      dispatch(resetVitalSigns());
    };
  }, [showDetails !== undefined]);

  return (
    <WithLoader
      isLoading={
        ![
          'post_measurement_plan',
          'get_measurement_plan',
          'vitalSigns/fetchAll',
          'vitalSigns/updateVitalSignByUserProfile',
        ].includes(vsResultsType) && vsResultsStatus === 'pending'
      }
    >
      {showDetails !== undefined && (
        <div className={styles.groupContainer}>
          <h2 className={styles.title}>{showDetails?.groupName}</h2>
          <div className={styles.downloadContainer}>
            {screen !== 'measurementTime' && (
              <div className={styles.downloadWrap} onClick={() => setOpenModal(true)}>
                <span>{translations('download')}</span>
                <img className={styles.downloadIcon} src={downloadIcon} alt="download" />
              </div>
            )}
            <BackButton
              style={{ width: 102, justifyContent: 'center', justifySelf: 'right', margin: 15 }}
              onClick={() => (screen === 'list' ? setShowDetails(undefined) : setScreen('list'))}
            />
          </div>
        </div>
      )}
      {showDetails === undefined ? (
        Object.entries(lastVitalsigns).length ? (
          <>
            <b>Latest</b>
            {Object.entries(lastVitalsigns)
              .sort(
                (a: [string, IVitalSign[]], b: [string, IVitalSign[]]) =>
                  new Date(b[1][0].datetime_measure).getTime() -
                  new Date(a[1][0].datetime_measure).getTime()
              )
              .map(([groupName, results]) => (
                <VitalSignResultsCard
                  key={results[0].vital_sign_group_id_out}
                  vitals={results}
                  groupName={groupName}
                  handleClick={(groupId: number) => setShowDetails({ groupName, groupId })}
                />
              ))
              .concat(
                noRecordVitalSigns
                  .filter(
                    (noRecord) =>
                      !Object.values(lastVitalsigns).some(
                        (results) => results[0]?.vital_sign_group_id_out === noRecord.groupId
                      )
                  )
                  .map((noRecordVitalSign) => (
                    <VitalSignResultsCard
                      key={noRecordVitalSign.groupId}
                      vitals={[]}
                      groupName={translations(`vitalSigns.${noRecordVitalSign.groupName}`)}
                      groupId={noRecordVitalSign.groupId}
                      handleClick={(groupId: number) =>
                        setShowDetails({ groupName: translations(`vitalSigns.${noRecordVitalSign.groupName}`), groupId })
                      }
                    />
                  ))
              )}
          </>
        ) : (
          <div className={styles.emptyStateContainer}>
            <b>No results</b>
            {noRecordVitalSigns.map((noRecordVitalSign) => (
              <VitalSignResultsCard
                key={noRecordVitalSign.groupId}
                vitals={[]}
                groupName={translations(`vitalSigns.${noRecordVitalSign.groupName}`)}
                groupId={noRecordVitalSign.groupId}
                handleClick={(groupId: number) =>
                  setShowDetails({ groupName: noRecordVitalSign.groupName, groupId })
                }
              />
            ))}
          </div>
        )
      ) : (
        <>
          {screen === 'list' && (
            <>
              <MainVitalScreen setScreen={setScreen} />
              <div style={{ marginTop: 20 }}>
                <b>History</b>
                {Object.keys(detailedData).length === 0 && (
                  <p className={styles.noResults}>{translations('noHistory')}</p>
                )}
                <CustomPagination
                  itemsPerPage={10}
                  items={Object.entries(detailedData).sort(
                    (a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime()
                  )}
                >
                  {(paginatedItems: any[]) => {
                    return (
                      <div>
                        {paginatedItems.map(([groupName, results]) => {
                          const groupedByDate: Record<string, any[]> = _.groupBy(results, (item) =>
                            moment(item.datetime_measure).format('DD.MM.YYYY - HH:mm')
                          );
                          return (
                            <CollapseV2 title={groupName} key={groupName}>
                              {Object.entries(groupedByDate)
                                .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
                                .map(([groupName, results]) => {
                                  return (
                                    <VitalSignResultsCard
                                      key={groupName}
                                      vitals={results}
                                      handleClick={() => handleVitalSignClick(results)}
                                    />
                                  );
                                })}
                            </CollapseV2>
                          );
                        })}
                      </div>
                    );
                  }}
                </CustomPagination>
              </div>
            </>
          )}
          {screen === 'report' && (
            <VitalSignReport report={vitalSignReport} personAccountNumber={personAccountNumber} />
          )}
          {screen === 'measurementTime' && showDetails && (
            <MeasurementTimes
              groupId={showDetails.groupId}
              setScreen={setScreen}
              personAccountNumber={personAccountNumber}
            />
          )}
          {screen === 'manualRecord' && (
            <ManualRecord
              group={showDetails}
              accountNumber={accountNumber}
              personId={personAccountNumber}
              setReport={handleSetReport}
            />
          )}
        </>
      )}
      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(!openModal)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        containerPadding={'10px 20px'}
        width="400px"
        hideCloseButton
      >
        <SelectTimeFrame
          onClose={() => setOpenModal(!openModal)}
          document_type="v"
          group_info={showDetails}
          customer_person_id={personAccountNumber}
        />
      </Modal>
    </WithLoader>
  );
};

export default VitalSignResultsV2;
